// src/components/staff/StaffTransfers.js
import React, { useState, useEffect } from 'react';
import { staffService } from '../../services/staffService';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import './StaffTransfers.css';

const StaffTransfers = ({ selectedStaffId = null, onClose }) => {
    usePageTitle('Staff Transfers');
    const { user } = useAuth();

    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const itemsPerPage = 20;

    // Filters
    const [filters, setFilters] = useState({
        staff_id: selectedStaffId || '',
        from_premises_id: '',
        to_premises_id: '',
        date_from: '',
        date_to: '',
        organization_id: ''
    });

    useEffect(() => {
        loadTransfers();
    }, [currentPage, filters]);

    const loadTransfers = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                ...filters,
                limit: itemsPerPage,
                offset: (currentPage - 1) * itemsPerPage
            };

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            const response = await staffService.getStaffTransfers(params);

            setTransfers(response.transfers || []);
            setTotalRecords(response.pagination?.total || 0);
            setTotalPages(response.pagination?.total_pages || 1);
        } catch (err) {
            console.error('Failed to load transfers:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setCurrentPage(1); // Reset to first page when filters change
    };

    const handleClearFilters = () => {
        setFilters({
            staff_id: selectedStaffId || '',
            from_premises_id: '',
            to_premises_id: '',
            date_from: '',
            date_to: '',
            organization_id: ''
        });
        setCurrentPage(1);
    };

    const handleExport = async () => {
        try {
            const exportFilters = { ...filters };
            Object.keys(exportFilters).forEach(key => {
                if (exportFilters[key] === '' || exportFilters[key] === null) {
                    delete exportFilters[key];
                }
            });

            await staffService.exportStaffTransfers('csv', exportFilters);
        } catch (err) {
            console.error('Failed to export transfers:', err);
            alert('Failed to export transfers: ' + err.message);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="staff-transfers-container">
            <div className="transfers-header">
                <div className="header-content">
                    <h2>
                        <i className="fas fa-exchange-alt"></i>
                        Staff Transfer History
                    </h2>
                    {onClose && (
                        <button className="btn btn-secondary" onClick={onClose}>
                            <i className="fas fa-times"></i>
                            Close
                        </button>
                    )}
                </div>

                <div className="transfers-stats">
                    <div className="stat-card">
                        <i className="fas fa-list"></i>
                        <div>
                            <span className="stat-label">Total Transfers</span>
                            <span className="stat-value">{totalRecords}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="transfers-filters">
                <div className="filters-grid">
                    <div className="filter-group">
                        <label>From Date</label>
                        <input
                            type="date"
                            name="date_from"
                            value={filters.date_from}
                            onChange={handleFilterChange}
                            className="filter-input"
                        />
                    </div>

                    <div className="filter-group">
                        <label>To Date</label>
                        <input
                            type="date"
                            name="date_to"
                            value={filters.date_to}
                            onChange={handleFilterChange}
                            className="filter-input"
                        />
                    </div>

                    <div className="filter-actions">
                        <button className="btn btn-secondary" onClick={handleClearFilters}>
                            <i className="fas fa-redo"></i>
                            Clear Filters
                        </button>
                        <button className="btn btn-primary" onClick={handleExport}>
                            <i className="fas fa-download"></i>
                            Export CSV
                        </button>
                    </div>
                </div>
            </div>

            {/* Transfers Table */}
            <div className="transfers-content">
                {loading ? (
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading transfers...</p>
                    </div>
                ) : error ? (
                    <div className="error-state">
                        <i className="fas fa-exclamation-circle"></i>
                        <p>{error}</p>
                        <button className="btn btn-primary" onClick={loadTransfers}>
                            Try Again
                        </button>
                    </div>
                ) : transfers.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-exchange-alt"></i>
                        <h3>No Transfer Records Found</h3>
                        <p>There are no staff transfer records matching your criteria.</p>
                    </div>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="transfers-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Staff Member</th>
                                        <th>Employee ID</th>
                                        <th>From Premises</th>
                                        <th>To Premises</th>
                                        <th>Remarks</th>
                                        <th>Transferred By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transfers.map((transfer) => (
                                        <tr key={transfer.id}>
                                            <td>{formatDate(transfer.transfer_date)}</td>
                                            <td>
                                                <div className="staff-info">
                                                    <strong>{transfer.staff_name}</strong>
                                                    {transfer.designation && (
                                                        <small>{transfer.designation}</small>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="employee-id">{transfer.employee_id}</span>
                                            </td>
                                            <td>
                                                <span className="premises-badge from">
                                                    {transfer.from_premises_name}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="premises-badge to">
                                                    <i className="fas fa-arrow-right"></i>
                                                    {transfer.to_premises_name}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="remarks-cell">
                                                    {transfer.remarks || '-'}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="transferred-by">
                                                    <i className="fas fa-user"></i>
                                                    {transfer.transferred_by_name}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                    Previous
                                </button>

                                <span className="page-info">
                                    Page {currentPage} of {totalPages} ({totalRecords} total records)
                                </span>

                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default StaffTransfers;
